// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Base stylesheet applied to the `EmblaCarousel` root wrapper.
 *
 * Exposes the carousel layout primitives (`.carousel`, `.carousel-container`,
 * `.carousel-slide`), the controls row (`.xng-pagination-container`), the
 * pagination dots (`.carousel-pagination`), and the custom scrollbar
 * (`.carousel-scrollbar`).
 *
 * Pulls colors and breakpoints from the XDL theme.
 */

import { color, xScreen } from "@stoneage/XDL/Utilities";
import { css } from "styled-components";

const SCROLLBAR_SIZE = "4px";
const SCROLLBAR_THUMB_WIDTH = "50px";
const SCROLLBAR_RADIUS = "2px";
const PAGINATION_DOT_SIZE = "10px";

const scrollbarThumb = css`
    position: absolute;
    border-radius: ${SCROLLBAR_RADIUS};
    transition: background-color 0.2s ease;
    cursor: grab;

    &:hover {
        background: ${color("common.ui8")};
    }

    &:active {
        cursor: grabbing;
    }
`;

const paginationDot = css`
    width: ${PAGINATION_DOT_SIZE};
    height: ${PAGINATION_DOT_SIZE};
    min-height: unset;
    padding: 0;
    margin: 0 !important;
    border: 1px solid ${color("common.ui4")};
    border-radius: 100%;
    background: transparent;
    opacity: 1;
    cursor: pointer;

    &:hover:not(.is-active) {
        background: ${color("common.ui3")};
        border-color: ${color("common.ui3")};
        outline: none;
    }

    &.is-active {
        background: ${color("common.ui5")};
        border-color: ${color("common.ui5")};
    }
`;

// ─── Main stylesheet ──────────────────────────────────────────────────────────

/**
 * Root CSS for the carousel. Composed into the `container` slot of the
 * default `carouselStyles` from `EmblaCarousel`.
 */
export const xCarouselStyle = css`
    width: 100%;
    padding-bottom: 30px;
    display: flex;
    flex-direction: column;

    /* ── Controls row ── */

    .xng-controls-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 12px;
        gap: 16px;

        & > div,
        & > button {
            margin: 0;
        }

        /* Scrollbar override when rendered inside the controls row */
        > .carousel-scrollbar.carousel-scrollbar-horizontal {
            height: ${SCROLLBAR_SIZE};
            width: 100%;
            position: relative;
            inset: unset;
            transform: initial;

            .carousel-scrollbar-thumb {
                background: ${color("common.black", 0.5)};
                width: ${SCROLLBAR_THUMB_WIDTH};
            }
        }

        &.inline {
            display: flex;
            align-items: center;
            position: absolute;
            gap: 4px;
            bottom: 24px;
            left: 24px;
            margin-top: 16px;
            z-index: 1;

            ${xScreen("lte", "md")} {
                left: 16px;
                bottom: 16px;
            }
        }
    }

    /* ── Pagination ── */

    .carousel-pagination {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 8px 4px;
        text-align: center;

        &.carousel-pagination-lock {
            display: none;
        }
    }

    /* ── Scrollbar ── */

    .carousel-scrollbar {
        background: transparent;
        border-radius: ${SCROLLBAR_RADIUS};

        &.carousel-scrollbar-lock {
            display: none;
        }

        .carousel-scrollbar-thumb {
            ${scrollbarThumb}
        }
    }

    .carousel-scrollbar-horizontal {
        .carousel-scrollbar-thumb {
            height: 100%;
        }
    }

    .carousel-scrollbar-vertical {
        width: ${SCROLLBAR_SIZE};

        .carousel-scrollbar-thumb {
            width: 100%;
        }
    }
`;
